import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ColorModeContext = createContext({ mode: 'light', toggleColorMode: () => {} });

/** Dashboard reports timezone — all session timestamps are normalized to this zone. */
export const DASHBOARD_TIMEZONE = 'Asia/Kolkata';

/** Shared brand tokens — use mode-specific palettes below for UI/chart consumption. */
export const brandColors = {
  primary: '#0C4DA2',
  primaryDark: '#0a4089',
  primaryLight: '#0095DA',
  primaryLightHover: '#007fba',
  neutral: '#9ca3af',
  error: '#d32f2f',
  pageBg: '#F2F2F2',
  badgeBg: '#dbeafe',
  badgeTextDark: '#93c5fd',
};

const statusTokensLight = {
  success: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  warning: { bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
  error: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  neutral: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
  info: { bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc' },
  brand: { bg: brandColors.badgeBg, text: brandColors.primary, border: brandColors.primaryLight },
};

const statusTokensDark = {
  success: { bg: 'rgba(34,197,94,0.15)', text: '#86efac', border: 'rgba(134,239,172,0.3)' },
  warning: { bg: 'rgba(234,179,8,0.15)', text: '#fde047', border: 'rgba(253,224,71,0.3)' },
  error: { bg: 'rgba(239,68,68,0.15)', text: '#fca5a5', border: 'rgba(252,165,165,0.3)' },
  neutral: { bg: 'rgba(100,116,139,0.2)', text: '#94a3b8', border: 'rgba(148,163,184,0.25)' },
  info: { bg: 'rgba(0, 149, 218, 0.2)', text: brandColors.primaryLight, border: 'rgba(0, 149, 218, 0.35)' },
  brand: { bg: 'rgba(0, 149, 218, 0.2)', text: brandColors.primaryLight, border: 'rgba(0, 149, 218, 0.35)' },
};

const semanticLight = {
  surface: {
    page: brandColors.pageBg,
    header: '#f8fafc',
    card: '#ffffff',
    cardSubtle: '#fafafa',
    cardTint: '#f5f9ff',
    input: '#ffffff',
    hover: '#F9FAFB',
    hoverSubtle: '#f3f4f6',
    overlay: '#08202d',
  },
  border: {
    default: 'rgba(0,0,0,0.08)',
    strong: '#e2e8f0',
    input: '#D0D5DD',
    inputHover: '#98A2B3',
  },
  text: {
    primary: '#111827',
    secondary: '#475569',
    muted: '#667085',
    label: '#344054',
    emphasis: '#101828',
    icon: '#98A2B3',
    onDark: '#ffffff',
  },
  action: {
    edit: { color: brandColors.primary, hoverBg: 'rgba(12, 77, 162, 0.1)' },
    delete: { color: brandColors.error, hoverBg: 'rgba(211, 47, 47, 0.1)' },
  },
  status: statusTokensLight,
  avatar: [brandColors.primary, brandColors.primaryLight, '#7c3aed', '#047857', '#b45309', '#be185d'],
};

const semanticDark = {
  surface: {
    page: '#0f172a',
    header: '#1e2530',
    card: 'rgba(255,255,255,0.03)',
    cardSubtle: 'rgba(255,255,255,0.03)',
    cardTint: '#111827',
    input: 'rgba(255,255,255,0.05)',
    hover: 'rgba(255,255,255,0.06)',
    hoverSubtle: 'rgba(255,255,255,0.04)',
    overlay: '#08202d',
  },
  border: {
    default: 'rgba(255,255,255,0.08)',
    strong: 'rgba(255,255,255,0.12)',
    input: 'rgba(255,255,255,0.18)',
    inputHover: 'rgba(255,255,255,0.28)',
  },
  text: {
    primary: '#f1f5f9',
    secondary: '#cbd5e1',
    muted: '#94a3b8',
    label: '#e2e8f0',
    emphasis: '#f8fafc',
    icon: '#94a3b8',
    onDark: '#ffffff',
  },
  action: {
    edit: { color: brandColors.primaryLight, hoverBg: 'rgba(0, 149, 218, 0.15)' },
    delete: { color: '#f87171', hoverBg: 'rgba(248, 113, 113, 0.15)' },
  },
  status: statusTokensDark,
  avatar: [brandColors.primaryLight, '#4db8e8', '#a78bfa', '#34d399', '#fbbf24', '#f472b6'],
};

export function getSemanticPalette(isDark) {
  return isDark ? semanticDark : semanticLight;
}

export function getStatusChipSx(variant, isDark, overrides = {}) {
  const palette = getSemanticPalette(isDark);
  const colors = palette.status[variant] || palette.status.neutral;
  return {
    bgcolor: colors.bg,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    fontWeight: variant === 'neutral' ? 500 : 600,
    fontSize: '0.75rem',
    height: 22,
    ...overrides,
  };
}

export function getRoleChipSx(roleName, isDark) {
  const name = (roleName || '').toLowerCase();
  if (name === 'admin') return getStatusChipSx('brand', isDark, { fontSize: '0.72rem' });
  if (name === 'manager') return getStatusChipSx('info', isDark, { fontSize: '0.72rem' });
  return getStatusChipSx('neutral', isDark, { fontSize: '0.72rem' });
}

export function getEditActionIconSx(isDark) {
  const { color, hoverBg } = getSemanticPalette(isDark).action.edit;
  return { color, borderRadius: '8px', p: 0.6, '&:hover': { bgcolor: hoverBg } };
}

export function getDeleteActionIconSx(isDark) {
  const { color, hoverBg } = getSemanticPalette(isDark).action.delete;
  return { color, borderRadius: '8px', p: 0.6, '&:hover': { bgcolor: hoverBg } };
}

const cssVarTokens = {
  '--nova-primary': brandColors.primary,
  '--nova-primary-dark': brandColors.primaryDark,
  '--nova-primary-light': brandColors.primaryLight,
  '--nova-error': brandColors.error,
  '--nova-page-bg': brandColors.pageBg,
  '--nova-badge-bg': brandColors.badgeBg,
  '--nova-border': '#e2e8f0',
  '--nova-text-primary': '#1e293b',
  '--nova-text-secondary': '#64748b',
  '--nova-text-muted': '#94a3b8',
  '--nova-surface': '#ffffff',
  '--nova-surface-elevated': '#f1f5f9',
  '--nova-surface-dark': '#1e293b',
  '--nova-surface-page-dark': '#0f172a',
  '--nova-input-bg': '#ffffff',
  '--nova-input-text': '#101828',
};

const cssVarTokensDark = {
  '--nova-page-bg': '#0f172a',
  '--nova-border': '#334155',
  '--nova-text-primary': '#f1f5f9',
  '--nova-text-secondary': '#94a3b8',
  '--nova-text-muted': '#64748b',
  '--nova-surface': '#1e293b',
  '--nova-surface-elevated': '#334155',
  '--nova-surface-dark': '#1e293b',
  '--nova-surface-page-dark': '#0f172a',
  '--nova-badge-bg': 'rgba(0, 149, 218, 0.2)',
  '--nova-input-bg': '#111827',
  '--nova-input-text': '#f8fafc',
};

export function applyThemeCssVars(mode) {
  const root = document.documentElement;
  const isDark = mode === 'dark';
  Object.entries(cssVarTokens).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  if (isDark) {
    Object.entries(cssVarTokensDark).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }
  // Form controls follow Nova only. "normal" would let the browser theme paint them.
  root.style.colorScheme = isDark ? 'dark' : 'light';
}

/** Darker tones for light backgrounds — better contrast on white / pale surfaces. */
const chartPaletteLight = {
  primary: '#0C4DA2',
  secondary: '#0095DA',
  tertiary: '#64748b',
  primaryDark: '#0a4089',
  positive: '#0C4DA2',
  neutral: '#0095DA',
  negative: '#64748b',
  error: '#c62828',
  primaryHover: '#0a4089',
  secondaryHover: '#0C4DA2',
  positiveHover: '#0a4089',
  neutralHover: '#0095DA',
  negativeHover: '#475569',
};

/** Lighter tones for dark backgrounds — better contrast on slate / navy surfaces. */
const chartPaletteDark = {
  primary: '#0095DA',
  secondary: '#4db8e8',
  tertiary: '#cbd5e1',
  primaryDark: '#0C4DA2',
  positive: '#0095DA',
  neutral: '#4db8e8',
  negative: '#94a3b8',
  error: '#f87171',
  primaryHover: '#4db8e8',
  secondaryHover: '#7cccf0',
  positiveHover: '#4db8e8',
  neutralHover: '#7cccf0',
  negativeHover: '#cbd5e1',
};

/** Default export for consumers that cannot access the MUI theme context. */
export const chartPalette = chartPaletteLight;

export function getChartPalette(isDark) {
  return isDark ? chartPaletteDark : chartPaletteLight;
}

export function getDashboardPalette(isDark) {
  const semantic = getSemanticPalette(isDark);
  return {
    pageBg: semantic.surface.page,
    badgeBg: semantic.status.brand.bg,
    badgeText: semantic.status.brand.text,
  };
}

function buildTheme(mode) {
  const isDark = mode === 'dark';
  const chart = getChartPalette(isDark);

  const primary = isDark
    ? { main: brandColors.primaryLight, dark: brandColors.primary, light: '#4db8e8' }
    : { main: brandColors.primary, dark: brandColors.primaryDark, light: brandColors.primaryLight };

  const error = isDark
    ? { main: '#f87171', dark: '#ef4444', light: '#fca5a5' }
    : { main: brandColors.error, dark: '#b71c1c', light: '#ef5350' };

  return createTheme({
    palette: {
      mode,
      primary,
      error,
      background: {
        default: isDark ? '#0f172a' : '#ffffff',
        paper: isDark ? '#111827' : '#ffffff',
      },
      text: {
        primary: isDark ? '#f1f5f9' : '#111827',
        secondary: isDark ? '#cbd5e1' : '#475569',
      },
      divider: isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
      chart: { ...chart },
      dashboard: getDashboardPalette(isDark),
      semantic: getSemanticPalette(isDark),
    },
    typography: {
      fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontWeight: 800 },
      h2: { fontWeight: 800 },
      h3: { fontWeight: 800 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 500 },
      subtitle2: { fontWeight: 500 },
      body1: { fontWeight: 500 },
      body2: { fontWeight: 400 },
      button: { fontWeight: 600 },
      caption: { fontWeight: 300 },
      overline: { fontWeight: 300 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: isDark ? 'dark' : 'light',
          },
          body: {
            fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '24px',
            fontSize: 13,
            boxShadow: 'none',
          },
          contained: {
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
            '&.Mui-disabled': {
              color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
              backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            backgroundColor: isDark ? brandColors.primaryLight : brandColors.primary,
            backgroundImage: 'none',
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDark ? '#4db8e8' : brandColors.primaryDark,
              backgroundImage: 'none',
              boxShadow: 'none',
            },
            '&:active': { boxShadow: 'none' },
            '&.Mui-disabled': {
              backgroundImage: 'none',
              backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
              boxShadow: 'none',
            },
          },
          containedError: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
          },
          outlined: {
            borderRadius: '24px',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            borderWidth: 1,
            '&:hover': {
              boxShadow: 'none',
              backgroundImage: 'none',
              backgroundColor: 'transparent',
            },
          },
          outlinedPrimary: {
            borderColor: isDark ? brandColors.primaryLight : brandColors.primary,
            color: isDark ? brandColors.primaryLight : brandColors.primary,
            '&:hover': {
              borderColor: isDark ? '#4db8e8' : brandColors.primaryDark,
              color: isDark ? '#4db8e8' : brandColors.primaryDark,
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
          outlinedError: {
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
          text: {
            borderRadius: '24px',
            boxShadow: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? 'rgba(8, 36, 72, 0.2)' : 'rgba(255, 255, 255, 0.16)',
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(0,149,218,0.06) 34%, rgba(8,36,72,0) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.02) 48%, rgba(0,149,218,0.04) 100%)',
            backdropFilter: 'blur(48px) saturate(160%)',
            WebkitBackdropFilter: 'blur(48px) saturate(160%)',
            color: isDark ? '#f4fbff' : brandColors.primaryDark,
            textShadow: isDark ? '0 1px 2px rgba(0,0,0,0.45)' : '0 1px 1px rgba(255,255,255,0.7)',
            border: isDark ? '1px solid rgba(0, 149, 218, 0.28)' : '1px solid rgba(12, 77, 162, 0.16)',
            borderRadius: 10,
            boxShadow: isDark
              ? 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,149,218,0.14), 0 8px 24px rgba(0,0,0,0.22)'
              : 'inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(12,77,162,0.06), 0 8px 24px rgba(12,77,162,0.08)',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.45,
            padding: '6px 10px',
          },
          arrow: {
            color: isDark ? 'rgba(8, 36, 72, 0.32)' : 'rgba(255, 255, 255, 0.28)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            backgroundColor: isDark ? '#111827' : '#f5f5f5',
            backgroundImage: 'none',
            color: isDark ? '#f8fafc' : '#111827',
            '&.MuiDialog-paperFullScreen': { borderRadius: 0 },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#111827' : '#f5f5f5',
            fontWeight: 700,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#111827' : '#f5f5f5',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#111827' : '#f5f5f5',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 12 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
    },
  });
}

export function useColorMode() {
  return useContext(ColorModeContext);
}

/** The widget iframe shares this origin. It must follow the browser, not the saved employee theme. */
function isWidgetDocument() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/widget';
}

/** Light or dark from the browser, so the embed matches the page the visitor is on. */
function browserColorScheme() {
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const widgetDocument = isWidgetDocument();
  const [mode, setMode] = useState(() => {
    if (widgetDocument) return browserColorScheme();
    const saved = localStorage.getItem('app-color-mode');
    if (saved === 'light' || saved === 'dark') return saved;
    return browserColorScheme();
  });

  useEffect(() => {
    // Saving here would overwrite the employee app's choice, because the iframe is the same origin.
    if (!widgetDocument) localStorage.setItem('app-color-mode', mode);
    const html = document.documentElement;
    html.setAttribute('data-theme', mode);
    applyThemeCssVars(mode);
  }, [mode, widgetDocument]);

  useEffect(() => {
    if (!widgetDocument) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const applyBrowserScheme = () => setMode(media.matches ? 'dark' : 'light');
    media.addEventListener('change', applyBrowserScheme);
    return () => media.removeEventListener('change', applyBrowserScheme);
  }, [widgetDocument]);

  // Flip the theme from the control the user clicked. The new theme blooms
  // outward at once. Browsers without view transitions switch immediately.
  const toggleColorMode = useCallback((event) => {
    const point = event?.nativeEvent || event;
    const x = typeof point?.clientX === 'number' ? point.clientX : window.innerWidth / 2;
    const y = typeof point?.clientY === 'number' ? point.clientY : 24;
    const apply = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || typeof document.startViewTransition !== 'function') {
      apply();
      return;
    }
    const radius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );
    const root = document.documentElement;
    root.style.setProperty('--theme-x', `${x}px`);
    root.style.setProperty('--theme-y', `${y}px`);
    root.style.setProperty('--theme-r', `${radius}px`);
    document.startViewTransition(() => {
      flushSync(apply);
    });
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleColorMode,
    }),
    [mode, toggleColorMode]
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
}
